import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../stores/authStore';
import { logoutAuth } from '../api/authApi';

const MENU_ITEMS = [
  { path: '/wallet', label: 'Wallet', icon: 'wallet' },
  { path: '/api-keys', label: 'API Keys', icon: 'key' },
  { path: '/settings', label: 'Settings', icon: 'settings' },
] as const;

function MenuIcon({ icon }: { icon: string }) {
  const cls = 'w-4 h-4';
  switch (icon) {
    case 'wallet':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="6" width="20" height="14" rx="2" /><path d="M2 10h20" />
          <rect x="16" y="12" width="4" height="4" rx="1" />
        </svg>
      );
    case 'key':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
      );
    case 'settings':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'logout':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16,17 21,12 16,7" /><line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      );
    default:
      return null;
  }
}

export function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isAuthenticated) {
    return (
      <button
        onClick={() => navigate('/login')}
        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-accent text-white hover:opacity-90 transition-opacity"
      >
        Login
      </button>
    );
  }

  const initial = (user?.displayName?.[0] ?? user?.email?.[0] ?? '?').toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent text-sm font-bold hover:bg-accent/30 transition-colors"
        title={user?.displayName ?? user?.email ?? 'Account'}
      >
        {initial}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-48 bg-bg-secondary border border-border rounded-lg shadow-lg py-1 z-50">
          {/* User info */}
          <div className="px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary truncate">{user?.displayName}</span>
              {user?.role === 'admin' && (
                <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent/20 text-accent">
                  Admin
                </span>
              )}
            </div>
            <div className="text-xs text-text-muted truncate">{user?.email}</div>
          </div>

          {/* Nav items */}
          {MENU_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setOpen(false); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors"
            >
              <MenuIcon icon={item.icon} />
              {item.label}
            </button>
          ))}

          {/* Logout */}
          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={() => { logoutAuth(); setOpen(false); navigate('/'); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-bearish hover:bg-bg-hover transition-colors"
            >
              <MenuIcon icon="logout" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
